import { useStore } from "@nanostores/preact";
import { useEffect, useState } from "preact/hooks";
import { type AssetDetail, ApiError, getAssetDetail, setPolicy } from "../net/api";
import { $panel, $policyCode, toast } from "../state/stores";
import { describeError } from "./errors";
import { Panel } from "./Panel";
import { formatShares, parseShares } from "./policy";
import { refreshMyAssets } from "../state/myAssets";

const LICENSES: Record<string, string> = { "cc0": "CC0（任何用途）", "cc-by": "CC BY（需標示作者）", "world-only": "僅限 Nomen Land 內使用" };

export function PolicyPanel() {
  const code = useStore($policyCode);
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [visibility, setVisibility] = useState("private");
  const [license, setLicense] = useState("");
  const [shares, setShares] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    getAssetDetail(code).then((a) => {
      setAsset(a);
      setVisibility(a.visibility);
      setLicense(a.license || "");
      setShares(formatShares(a.shares ?? []));
    }).catch((e) => setError(e instanceof ApiError ? describeError(e.code) : "無法連線到伺服器"));
  }, [code]);
  if (!code) return null;

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await setPolicy(code!, visibility, visibility === "private" ? "" : license, visibility === "shared" ? parseShares(shares) : []);
      await refreshMyAssets();
      toast("政策已更新並寫入帳本");
      $panel.set("inventory");
    } catch (err) {
      setError(err instanceof ApiError ? describeError(err.code) : "無法連線到伺服器");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={`使用政策 ${code}`} onClose={() => $panel.set("inventory")}>
      {!asset && !error && <p class="hint">載入中…</p>}
      {asset && (
        <form onSubmit={save}>
          <p>{asset.name} · 目前在 {asset.used_in_plots} 塊領地中使用</p>
          <label>誰能擺放</label>
          <select value={visibility} onChange={(e) => setVisibility((e.target as HTMLSelectElement).value)}>
            <option value="private">只有我（private）</option>
            <option value="shared">我指定的帳號與領地（shared）</option>
            <option value="public">任何人，並可被搜尋（public）</option>
          </select>
          {visibility !== "private" && (
            <>
              <label>授權</label>
              <select value={license} required onChange={(e) => setLicense((e.target as HTMLSelectElement).value)}>
                <option value="" disabled>請選擇</option>
                {Object.entries(LICENSES).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </>
          )}
          {visibility === "shared" && (
            <>
              <label>分享對象（每行一個 email、帳號 id，或 plot:領地 id）</label>
              <textarea rows={4} value={shares} onInput={(e) => setShares((e.target as HTMLTextAreaElement).value)} />
            </>
          )}
          <p class="hint">private 不是機密：走進領地看到模型的人，瀏覽器都已下載檔案。政策只決定誰能擺放，並記錄在帳本裡。改回 private 不會移除已經擺出去的實例。</p>
          <button type="submit" disabled={busy}>儲存</button>
          {error && <div class="error">{error}</div>}
        </form>
      )}
      {!asset && error && <div class="error">{error}</div>}
    </Panel>
  );
}
